/**
 * Banque de liens — module pur, sans React.
 *
 * Le back sert des **groupes** déjà constitués et déjà triés : un dossier par
 * lien d'entreprise ou de métier, une personne pour les liens personnels. Le
 * front n'en refait ni le tri ni le filtrage — ce que l'utilisateur n'a pas le
 * droit de voir n'arrive jamais jusqu'ici, c'est la policy du back qui le
 * garantit.
 */

export const LINK_SCOPES = ['company', 'company_work', 'user'];

/** Le groupe de mes propres liens porte mon nom côté back : ici, c'est « moi ». */
const MY_LINKS_LABEL = 'Mes liens';

/** Un lien d'une réponse d'API → lien exploitable, ou `null` s'il est inutilisable. */
function normalizeWebLink(raw) {
  if (!raw || raw.id == null || !raw.url) return null;
  return {
    id: raw.id,
    label: raw.label || raw.url,
    url: raw.url,
    description: raw.description || '',
    tags: Array.isArray(raw.tags) ? raw.tags : [],
    scope: LINK_SCOPES.includes(raw.scope) ? raw.scope : 'company',
    ownerName: raw.owner_name || '',
    folderId: raw.web_link_folder_id ?? null,
  };
}

/**
 * Une réponse d'API → groupes exploitables.
 *
 * Le back sert des **groupes**, pas une liste plate : un dossier par lien
 * d'entreprise ou de métier, une personne pour les liens personnels — on ne
 * veut pas qu'un utilisateur se fabrique plusieurs tiroirs personnels. L'ordre
 * est décidé par le back (dossiers par nom, puis personnes par nom) et le front
 * ne le rejoue pas.
 *
 * Le seul habillage fait ici : le groupe de mes propres liens s'appelle
 * « Mes liens » et non mon nom complet — le back ne sait pas qui regarde.
 *
 * @param {Array} raw
 * @param {number|string} [currentUserId] l'utilisateur connecté
 */
export function normalizeWebLinkGroups(raw, currentUserId) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((group) => {
      const links = (Array.isArray(group?.web_links) ? group.web_links : [])
        .map(normalizeWebLink)
        .filter(Boolean);
      if (!links.length) return null;
      const kind = group.kind === 'user' ? 'user' : 'folder';
      return {
        key: `${kind}:${group.id}`,
        kind,
        id: group.id,
        name: isMe(kind, group.id, currentUserId)
          ? MY_LINKS_LABEL
          : (group.name || ''),
        scope: LINK_SCOPES.includes(group.scope) ? group.scope : 'company',
        links,
      };
    })
    .filter(Boolean);
}

function isMe(kind, groupId, currentUserId) {
  if (kind !== 'user' || groupId == null || currentUserId == null) return false;
  return String(groupId) === String(currentUserId);
}

/** Les liens de tous les groupes, à plat — pour retrouver un lien par son id. */
export function flattenWebLinks(groups) {
  return (groups || []).flatMap((group) => group.links);
}

/**
 * Groupes → groupes du `Select`, dans l'ordre servi par le back.
 *
 * @param {Array} groups
 * @param {(link: object) => boolean} [isEligible] filtre optionnel (contenu externe)
 */
export function buildWebLinkOptions(groups, isEligible) {
  return (groups || [])
    .map((group) => ({
      group: group.name,
      items: (isEligible ? group.links.filter(isEligible) : group.links).map((link) => ({
        value: String(link.id),
        label: link.label,
        description: link.description,
        url: link.url,
      })),
    }))
    // Un groupe vidé par le filtre d'éligibilité ne doit pas laisser un en-tête
    // seul dans la liste.
    .filter((group) => group.items.length > 0);
}

/** Le lien de la banque désigné par un id, ou `null`. */
export function findWebLink(links, id) {
  if (id == null || id === '') return null;
  return links.find((link) => String(link.id) === String(id)) || null;
}

/**
 * Rafraîchit les cibles de bouton branchées sur la banque, dans toute une
 * structure (web_data, données Puck d'une page, section de squelette).
 *
 * Même règle que les variables (`resolveVariableNodesDeep`) et même contrat de
 * référence : la **même référence** est rendue quand rien ne change, pour que
 * l'appelant n'écrive ni ne remonte Puck pour rien.
 *
 * - lien connu → son adresse et son libellé sont réécrits ;
 * - lien supprimé de la banque → l'adresse copiée est **conservée** (le site
 *   publié ne doit pas casser) et le lien est marqué orphelin, signalé dans
 *   l'éditeur seulement.
 */
export function resolveBankLinksDeep(value, byId) {
  if (typeof value === 'string') {
    return resolveBankLinksInHtml(value, byId);
  }
  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map((item) => {
      const resolved = resolveBankLinksDeep(item, byId);
      if (resolved !== item) changed = true;
      return resolved;
    });
    return changed ? next : value;
  }
  if (value && typeof value === 'object') {
    if (isBankLink(value)) return resolveBankLink(value, byId);
    let changed = false;
    const next = {};
    Object.entries(value).forEach(([prop, item]) => {
      const resolved = resolveBankLinksDeep(item, byId);
      if (resolved !== item) changed = true;
      next[prop] = resolved;
    });
    return changed ? next : value;
  }
  return value;
}

/* Une ancre de texte riche qui vient de la banque porte son identifiant, posé
   par `tiptap/bankLinkExtension`. Une adresse tapée à la main n'en a pas, et
   n'est donc jamais réécrite. */
const BANK_ANCHOR_RE = /<a\b[^>]*\bdata-link-id="[^"]*"[^>]*>/gi;
const HREF_RE = /\bhref="([^"]*)"/i;
const LINK_ID_RE = /\bdata-link-id="([^"]*)"/i;

/* Même échappement que la sérialisation DOM de Tiptap (`editor.getHTML()`) :
   sinon un rafraîchissement réécrirait des ancres identiques à l'infini. */
function escapeHref(url) {
  return String(url ?? '').replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}

/**
 * Réécrit l'adresse des ancres de texte riche issues de la banque.
 *
 * Un lien supprimé de la banque **garde son adresse** — le site publié ne doit
 * pas casser — exactement comme une cible de bouton orpheline.
 */
export function resolveBankLinksInHtml(html, byId) {
  if (!html.includes('data-link-id=')) return html;

  let changed = false;
  const next = html.replace(BANK_ANCHOR_RE, (anchor) => {
    const id = anchor.match(LINK_ID_RE)?.[1];
    const known = id ? byId[String(id)] : null;
    if (!known) return anchor;

    const href = escapeHref(known.url);
    const rewritten = anchor.match(HREF_RE)
      ? anchor.replace(HREF_RE, `href="${href}"`)
      : anchor.replace(/^<a\b/i, `<a href="${href}"`);
    if (rewritten !== anchor) changed = true;
    return rewritten;
  });

  return changed ? next : html;
}

/** Les liens de la banque, indexés par id en chaîne. */
export function indexWebLinks(links) {
  return Object.fromEntries((links || []).map((link) => [String(link.id), link]));
}

/**
 * Deux formes portent un lien de la banque, et il faut les distinguer :
 *
 * - la cible d'un **bouton**, un objet `link` qui se déclare par son `kind` et
 *   porte un `linkLabel` ;
 * - les props d'un **contenu externe**, qui n'ont pas de `kind` : le bloc n'a
 *   qu'une adresse, et `linkId` dit d'où elle vient.
 *
 * Le second cas se reconnaît au couple `linkId` + `url` — assez spécifique
 * pour qu'aucune autre prop du projet n'y ressemble.
 */
function isBankLink(value) {
  if (value.linkId === undefined || value.linkId === '' || value.linkId === null) return false;
  return value.kind === 'bank' || (value.kind === undefined && typeof value.url === 'string');
}

function resolveBankLink(link, byId) {
  const known = byId[String(link.linkId)];
  const next = known
    ? {
      ...link,
      url: known.url,
      // Un contenu externe n'affiche pas de libellé : ne pas lui en inventer un.
      ...(link.kind === 'bank' ? { linkLabel: known.label } : {}),
      linkOrphan: false,
    }
    : { ...link, linkOrphan: true };

  const changed = next.url !== link.url
    || next.linkLabel !== link.linkLabel
    || next.linkOrphan !== (link.linkOrphan === true);
  return changed ? next : link;
}
